/**
 * @file   src\Pages\ProductSignUpCarInfo.tsx
 * @brief  Product signup car info step when car details are required.
 * @date   JUL, 2026
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  React, useNavigate, useParams, useState, useEffect, Link, useLocation
} from "../utils/thirdpartyComponents";
import { Button, Modal } from "react-bootstrap";
import CWInput from "src/components/CWInput";
import "src/styles/inner-page-styles.scss";
import { pageURLs } from "src/utils/pageUrls";
import { SIGNUP_FOR_FAMILYCODE_ERROR, SUBJECT_MAX_LENGTH } from "src/utils/constants";
import { ISignupData } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { SignupApi } from "../utils/apiUrls";
import { MessageToaster } from "src/utils/toastUtil";
import SmallLoader from "../components/SmallLoader";
import { formatPhoneNumberforapipass, PRODUCT_SIGNUP_DATA_KEY } from "../utils/helper";
import { getCompanySettings } from 'src/store/actions/settingsAction';
import { useAppDispatch } from 'src/hooks';
import ProductSignUpVerificationModal from "src/components/ProductSignUpVerificationModal";
import { buildSignupWithCarInfoSchema } from "src/validations/SignupWithCarInfoSchema";
import { getActiveCountryCode } from "src/utils/countryHelper";
import Gobackicon from "../assets/images/left-arrow-small.svg";
import { CommonMessages } from "src/messages/validationMessages";

const ProductSignUpCarInfo = () => {
  const toaster = new MessageToaster();
  const navigate = useNavigate();
  const params = useParams();
  const shareCode = params.shareCode || '';
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);
  const [showFamilyCodeModal, setShowFamilyCodeModal] = useState(false);
  const [noformatPhone, setnoformatPhone] = useState('');

  const [formData, setFormData] = useState<ISignupData & {
    carMake: string;
    carModel: string;
    carColor: string;
    carLicensePlatNumber: string;
  }>({
    name: '',
    phone: '',
    email: '',
    appliedReferralCode: '',
    receivesms: false,
    referralType: 0,
    carWashId: 0,
    carMake: '',
    carModel: '',
    carColor: '',
    carLicensePlatNumber: '',
  });

  const [errors, setErrors] = useState<any>({});

  const handleCloseVerificationModal = () => setShowVerificationModal(false);
  const handleFamilyCodeModalClose = () => setShowFamilyCodeModal(false);

  const handleFamilyCodeNo = () => {
    setShowFamilyCodeModal(false);
    toaster.toastError(CommonMessages.signup_for_familycode_error);
  };

  const handleFamilyCodeYes = async () => {
    setShowFamilyCodeModal(false);

    let carWashId = Number(localStorage.getItem('carWashId')) || 0;
    if (!carWashId) {
      await dispatch(getCompanySettings({ carwashname: params?.carwashname || "" }));
      carWashId = Number(localStorage.getItem('carWashId')) || 0;
    }

    if (!carWashId) {
      toaster.toastError("Could not process the request. Please try again later.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(SignupApi.APPLY_FAMILY_CODE, {
        phoneNumber: formatPhoneNumberforapipass(formData.phone),
        carWashId,
        carWashURL: params.carwashname,
        familyCode: formData.appliedReferralCode,
      });
      if (data?.error === 0) {
        toaster.toastSuccess(data?.message);
        navigate("/" + params.carwashname + pageURLs.root);
      } else {
        toaster.toastError(data?.message);
      }
    } catch (error: any) {
      toaster.toastError(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fromState = location.state;
    const fromStorage = JSON.parse(localStorage.getItem(PRODUCT_SIGNUP_DATA_KEY) || "{}");
    setFormData(prev => ({ ...prev, ...fromStorage, ...fromState }));
  }, []);

  useEffect(() => {
    const previous = JSON.parse(localStorage.getItem(PRODUCT_SIGNUP_DATA_KEY) || "{}");
    localStorage.setItem(PRODUCT_SIGNUP_DATA_KEY, JSON.stringify({ ...previous, ...formData }));
  }, [formData]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setErrors((prev: any) => ({ ...prev, [name]: '' }));
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = { ...formData };
      await buildSignupWithCarInfoSchema(getActiveCountryCode()).validate(payload, { abortEarly: false });
      payload.phone = formatPhoneNumberforapipass(formData.phone);
      payload.CarWashURL = params.carwashname;
      payload.carWashId = Number(localStorage.getItem('carWashId')) || 0;
      setnoformatPhone(payload.phone);

      if (!payload.carWashId) {
        await dispatch(getCompanySettings({ carwashname: params?.carwashname || "" }));
        payload.carWashId = Number(localStorage.getItem('carWashId')) || 0;
      }

      const { data } = await axios.post(SignupApi.SIGN_UP, payload);
      localStorage.removeItem(PRODUCT_SIGNUP_DATA_KEY);

      if (data?.error === 0) {
        setShowVerificationModal(true);
        setErrors({ name: '', phone: '', email: '', referralCode: '' });
      } else if (data?.error === SIGNUP_FOR_FAMILYCODE_ERROR) {
        setShowFamilyCodeModal(true);
      } else {
        toaster.toastError(data?.message);
      }
    } catch (err: any) {
      if (err.inner) {
        const errs: any = {};
        err.inner.forEach((e: any) => {
          errs[e.path] = e.message;
        });
        setErrors(errs);
      } else {
        toaster.toastError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="common-content-wrap pb-170">
      <form onSubmit={handleSubmit}>
        <div className="inner-content-wrap">
          <div className="common-header two-block-header">
            <div className="pe-4 ps-4 pt-0 pb-0 h-100">
              <div className="common-header-inner">
                <Link to={`/${params.carwashname}/${pageURLs.product_signup}/${shareCode}`} className="back-link">
                  <img src={Gobackicon} alt="Car Wash App" />
                </Link> Car Info
              </div>
            </div>
          </div>

          <div className="main-content-wrap pe-0 ps-0 pt-0">
            <div className="profile-content-wrap">
              <CWInput id="carMake" name="carMake" label="Make" isMandatory maxLength={SUBJECT_MAX_LENGTH} onChange={handleInputChange} errorMessage={errors.carMake} value={formData.carMake} />
              <CWInput id="carModel" name="carModel" label="Model" isMandatory maxLength={SUBJECT_MAX_LENGTH} onChange={handleInputChange} errorMessage={errors.carModel} value={formData.carModel} />
              <CWInput id="carColor" name="carColor" label="Color" isMandatory maxLength={SUBJECT_MAX_LENGTH} onChange={handleInputChange} errorMessage={errors.carColor} value={formData.carColor} />
              <CWInput id="carLicensePlatNumber" name="carLicensePlatNumber" label="License Plate Number" isMandatory maxLength={SUBJECT_MAX_LENGTH} onChange={handleInputChange} errorMessage={errors.carLicensePlatNumber} value={formData.carLicensePlatNumber} />
              <Button variant="primary" className="primary-btn btn-main mt-4" type="submit">Complete Sign Up</Button>
            </div>
          </div>
        </div>
      </form>

      <ProductSignUpVerificationModal
        show={showVerificationModal}
        onHide={handleCloseVerificationModal}
        onModalHide={handleCloseVerificationModal}
        phoneNumber={noformatPhone}
      />
      <Modal show={showFamilyCodeModal} onHide={handleFamilyCodeModalClose} centered className='notification-modal'>
        <Modal.Body>
          You already have an account. Do you want to apply this Family Plan code to your existing account?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleFamilyCodeNo}>Cancel</Button>
          <Button variant="primary" className="primary-btn" onClick={handleFamilyCodeYes}>Apply Code</Button>
        </Modal.Footer>
      </Modal>
      {loading ? <SmallLoader /> : ''}
    </div>
  );
};

export default ProductSignUpCarInfo;
